import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Input } from '@upme/ui/components/input';
import { Label } from '@upme/ui/components/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@upme/ui/components/select';
import { NewsTable } from '../../../features/news/components/news-table';
import { type AdminNewsListFilters, adminNewsListQueryOptions } from '../../../features/news/queries';
import { PageHeader } from '../../../shared/components/page-header';
import { Route as NewsRoute } from './route';

const ANY = '__any__';

function toFilters(search: ReturnType<typeof NewsRoute.useSearch>): AdminNewsListFilters {
  const out: AdminNewsListFilters = {};
  if (search.status !== undefined) out.status = search.status;
  if (search.search !== undefined && search.search !== '') out.search = search.search;
  return out;
}

export const Route = createFileRoute('/_authenticated/news/')({
  loaderDeps: ({ search }) => ({ status: search.status, search: search.search }),
  loader: async ({ context, deps }) => {
    await context.queryClient.ensureQueryData(
      adminNewsListQueryOptions(toFilters({ status: deps.status, search: deps.search })),
    );
  },
  component: NewsIndexPage,
});

function NewsIndexPage() {
  const search = NewsRoute.useSearch();
  const navigate = useNavigate();
  const { data: list } = useSuspenseQuery(adminNewsListQueryOptions(toFilters(search)));

  const update = (patch: Record<string, string | undefined>) => {
    navigate({
      to: '/news',
      search: () => {
        const next: Record<string, string | undefined> = {
          status: search.status,
          search: search.search,
          ...patch,
        };
        const out: Record<string, string> = {};
        for (const [k, v] of Object.entries(next)) {
          if (v !== undefined && v !== '' && v !== null) out[k] = v;
        }
        return out;
      },
    });
  };

  return (
    <div>
      <PageHeader title="Новости" description="Автоматически опубликованные новости из Telegram-каналов" />

      <div className="border rounded-md p-4 mb-4 grid gap-4 grid-cols-1 md:grid-cols-2">
        <div>
          <Label className="text-xs">Статус</Label>
          <Select
            value={search.status ?? ANY}
            onValueChange={(v) => update({ status: v === ANY ? undefined : v })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Все" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Все</SelectItem>
              <SelectItem value="PUBLISHED">Опубликовано</SelectItem>
              <SelectItem value="HIDDEN">Скрыто</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Поиск</Label>
          <Input
            placeholder="Поиск по заголовку…"
            value={search.search ?? ''}
            onChange={(e) => update({ search: e.target.value || undefined })}
          />
        </div>
      </div>

      <div className="text-sm text-muted-foreground mb-2">Всего: {list.total}</div>

      <NewsTable rows={list.rows} />
    </div>
  );
}
